/**
 * In-page section anchors (#id) — fixed top gap by scroll direction (desktop only).
 * Desktop: 180px up, 63px down. Mobile: 0px.
 */
(function () {
  const MOBILE_MQ = '(max-width: 989px)';
  const OFFSET_SCROLL_UP = 180;
  const OFFSET_SCROLL_DOWN = 63;
  const MOBILE_OFFSET = 0;
  const SCROLL_DIRECTION_THRESHOLD = 5;

  /** @type {HTMLStyleElement | null} */
  let styleEl = null;

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {string} href
   * @returns {string | null}
   */
  function parseAnchorId(href) {
    if (!href) return null;
    const hashIndex = href.indexOf('#');
    if (hashIndex === -1) return null;
    const id = decodeURIComponent(href.slice(hashIndex + 1).trim());
    return id || null;
  }

  /**
   * @param {HTMLAnchorElement} link
   * @returns {boolean}
   */
  function isSamePageAnchor(link) {
    const href = link.getAttribute('href');
    if (!href) return false;

    if (href.startsWith('#')) return true;

    try {
      const url = new URL(href, window.location.href);
      return url.pathname === window.location.pathname && url.search === window.location.search && url.hash.length > 1;
    } catch {
      return false;
    }
  }

  /**
   * @param {HTMLElement} targetEl
   * @returns {boolean}
   */
  function isScrollingUpTo(targetEl) {
    const scrollTop = window.scrollY;
    const targetTop = targetEl.getBoundingClientRect().top + scrollTop;
    return targetTop < scrollTop - SCROLL_DIRECTION_THRESHOLD;
  }

  /**
   * @param {HTMLElement} targetEl
   * @returns {number}
   */
  function getScrollOffset(targetEl) {
    if (isMobile()) return MOBILE_OFFSET;
    return isScrollingUpTo(targetEl) ? OFFSET_SCROLL_UP : OFFSET_SCROLL_DOWN;
  }

  function updateScrollMarginVar() {
    const offset = isMobile() ? MOBILE_OFFSET : OFFSET_SCROLL_UP;
    document.documentElement.style.setProperty('--pb-anchor-scroll-offset', `${offset}px`);
  }

  function ensureStyles() {
    if (styleEl) return;

    styleEl = document.createElement('style');
    styleEl.textContent = `
      [data-pb-anchor-target] {
        scroll-margin-top: var(--pb-anchor-scroll-offset, ${OFFSET_SCROLL_UP}px);
      }
      @media ${MOBILE_MQ} {
        [data-pb-anchor-target] {
          scroll-margin-top: ${MOBILE_OFFSET}px;
        }
      }
    `;
    document.head.appendChild(styleEl);
  }

  /**
   * @param {HTMLElement} target
   * @param {ScrollBehavior} [behavior]
   */
  function scrollToTarget(target, behavior) {
    const offset = getScrollOffset(target);
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: behavior ?? (prefersReducedMotion() ? 'auto' : 'smooth'),
    });
  }

  /**
   * @param {string} id
   */
  function scrollToId(id) {
    const target = document.getElementById(id);
    if (!target) return false;
    scrollToTarget(target);
    return true;
  }

  function handleInitialHash() {
    // SMYLE CTA just set the hash — ignore so we do not overshoot after a correct scroll.
    if (Date.now() < (window.__pbSmyleIgnoreHashScrollUntil || 0)) return;

    const id = parseAnchorId(window.location.hash);
    if (!id) return;

    const target = document.getElementById(id);
    if (!target) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToTarget(target, 'auto');
      });
    });
  }

  function onDocumentClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement) || !isSamePageAnchor(link)) return;

    // SMYLE FAQ CTA uses its own sticky-nav offset scroll — do not double-handle.
    if (link.hasAttribute('data-pb-smyle-faq-cta') || link.closest('.pb-smyle-faq-cta')) return;

    const id = parseAnchorId(link.getAttribute('href') ?? '');
    if (!id) return;

    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();

    const nextUrl = `${window.location.pathname}${window.location.search}#${encodeURIComponent(id)}`;
    history.pushState(null, '', nextUrl);
    scrollToTarget(target);
  }

  function init() {
    ensureStyles();
    updateScrollMarginVar();

    document.addEventListener('click', onDocumentClick, true);
    window.addEventListener('hashchange', handleInitialHash);
    window.addEventListener('resize', updateScrollMarginVar, { passive: true });

    handleInitialHash();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.PBAnchorScroll = {
    scrollToId,
    getScrollOffset,
    OFFSET_SCROLL_UP,
    OFFSET_SCROLL_DOWN,
    MOBILE_OFFSET,
  };
})();
